<script setup lang="ts">
// /system-types — mockup page 5 (M7).
//
// The comparison is the page, and the sidebar matcher turns it into a lead:
// four questions from the askhvac.system_match contract, walked with the
// shared TreeWalker, ending on a tier recommendation that rides to the
// Second Opinion form for the server to replay.
//
// Prices are deliberately absent — see content/systems.ts.
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

import TreeWalker from '@/shared/components/TreeWalker.vue'
import type { Answer, DecisionTree, TreeNode } from '@/shared/diagnostic/types'
import treeJson from '@contracts/decision-tree.askhvac-system-match.v1.json'
import { leadIntent } from '@/shared/state/leadIntent'
import { contactContext, contactContextLabel } from '../store'
import { FEATURES, TIERS } from '../content/systems'
// Pixabay Content License — see src/assets/ATTRIBUTIONS.md.
import heroPhoto from '@/assets/ror-condensers.jpg'

const tree = treeJson as unknown as DecisionTree

const matching = ref(false)
const walker = ref<InstanceType<typeof TreeWalker> | null>(null)
const outcome = ref<TreeNode | null>(null)

function startMatch() {
    matching.value = true
    outcome.value = null
    walker.value?.reset()
}

function onOutcome(_id: string, node: TreeNode, answers: Answer[]) {
    outcome.value = node
    contactContext.value = { tree_id: tree.id, answers }
    contactContextLabel.value = tree.title ?? tree.id
    // Someone who has just chosen between new systems has said, by their own
    // navigation, that they're buying one. That's a visitor statement, not an
    // inference from symptoms, so it's recorded as stated intent.
    leadIntent.value = 'replace'
}

function restartMatch() {
    outcome.value = null
    walker.value?.reset()
}

const INSIST = [
    'A written load calculation (Manual J), not a rule-of-thumb size',
    'Model numbers for the indoor and outdoor units on the quote',
    'Warranty terms in writing, including the registration deadline',
    'Who services this equipment locally, by name',
    'Sized for Florida humidity, not just Florida heat',
]

const HERO_BADGES = [
    'Honest comparisons',
    'No equipment to sell',
    'Comfort and efficiency explained',
    'Long-term value',
]

const MATCH_CRITERIA = [
    'Home size and layout',
    'What actually bothers you today',
    'Efficiency goals',
    'Budget range',
]

const FLORIDA = [
    {
        title: 'Hot, humid climate',
        body: 'Removing moisture matters as much as removing heat here. A system that cools fast and shuts off leaves the house cold and damp — which is why capacity control shows up so high in the table.',
        icon: '<path d="M10 1.6a1 1 0 0 1 1 1v6.2a3.4 3.4 0 1 1-2 0V2.6a1 1 0 0 1 1-1zm3.6 2.2 1.3 1.3-1.7 1.7-1.3-1.3zm2.6 4.4h2.4v1.8h-2.4z"/>',
    },
    {
        title: 'A long cooling season',
        body: 'Efficiency is bought once and repaid monthly. Florida runs its air conditioning for more of the year than most of the country, so the repayment period is shorter here than the national averages suggest.',
        icon: '<path d="M11 2 4 12h5l-1 6 7-10h-5z"/>',
    },
    {
        title: 'Salt, storms and run hours',
        body: 'Coastal air is hard on outdoor coils and long run hours are hard on everything. Ask about coil protection and what the warranty says about corrosion before you compare on price alone.',
        icon: '<path d="M10 1.6 3 4.6v5.2c0 4.3 3 8.3 7 9.2 4-.9 7-4.9 7-9.2V4.6z"/>',
    },
]

const BENEFITS = [
    {
        title: 'Lower running cost',
        body: 'A modern system does the same job for less electricity — the gap is biggest against equipment over ten years old.',
        icon: '<path d="M11 2 4 12h5l-1 6 7-10h-5z"/>',
    },
    {
        title: 'Steadier comfort',
        body: 'Longer, gentler run cycles hold temperature and pull humidity out instead of blasting and stopping.',
        icon: '<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>',
    },
    {
        title: 'Quieter operation',
        body: 'Most of the noise comes from running at full capacity. Systems that rarely need full capacity are rarely loud.',
        icon: '<path d="M4 8h2.6L10 4.8v10.4L6.6 12H4z"/><path d="M12.6 7.4a3.6 3.6 0 0 1 0 5.2" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/>',
    },
    {
        title: 'Better diagnostics',
        body: 'Newer equipment reports its own faults, which shortens service calls and makes a second opinion easier to check.',
        icon: '<path d="M4 2.8h12a1 1 0 0 1 1 1v12.4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3.8a1 1 0 0 1 1-1zm1.8 3.4v1.6h8.4V6.2zm0 3.6v1.6h8.4V9.8zm0 3.6v1.6h5.2v-1.6z"/>',
    },
]
</script>

<template>
    <section class="hero" :style="{ backgroundImage: `url(${heroPhoto})` }">
        <div class="wash" aria-hidden="true"></div>
        <div class="hero-inner">
            <div>
                <p class="crumb">Home <span>›</span> System Types</p>
                <h1>Which AC System<br />Is <span class="hl">Right for You?</span></h1>
                <p class="lede">
                    Compare the three tiers on what actually separates them — how precisely each
                    one controls capacity — so you can tell which differences are worth paying for
                    in your house.
                </p>
                <ul class="badges">
                    <li v-for="b in HERO_BADGES" :key="b">{{ b }}</li>
                </ul>
            </div>

            <aside class="insist">
                <h2>What to insist on, whatever you buy</h2>
                <ul>
                    <li v-for="i in INSIST" :key="i">{{ i }}</li>
                </ul>
                <p class="note">
                    We don't sell, install or warrant equipment. This is the list to hold an
                    installer to.
                </p>
            </aside>
        </div>
    </section>

    <section class="compare">
        <div class="compare-inner">
            <h2 class="centered">Compare AC system types</h2>

            <div class="tiers">
                <article v-for="t in TIERS" :key="t.id" class="tier" :data-tier="t.id">
                    <span class="ribbon">{{ t.ribbon }}</span>
                    <h3>{{ t.name }}</h3>
                    <p class="tagline">{{ t.tagline }}</p>
                    <ul>
                        <li v-for="p in t.points" :key="p">{{ p }}</li>
                    </ul>
                    <div class="best">
                        <strong>Best for:</strong>
                        {{ t.bestFor }}
                    </div>
                    <!-- Relative cost only, permanently: no tier-by-tier price
                         table on this site. See content/systems.ts. -->
                    <div class="price">
                        <span class="label">Relative upfront cost</span>
                        <strong class="tierprice">{{ t.cost }}</strong>
                        <small>
                            Compared with the other two tiers, not a price. See the
                            <RouterLink to="/cost-guides#new-system">planning range</RouterLink>
                            for what a replacement generally runs.
                        </small>
                    </div>
                </article>

                <aside class="matcher">
                    <h3>Not sure which is right?</h3>
                    <p>
                        Four questions. We'll say which tier fits — including when the cheapest one
                        is the right answer.
                    </p>

                    <ul v-if="!matching" class="criteria">
                        <li v-for="c in MATCH_CRITERIA" :key="c">{{ c }}</li>
                    </ul>
                    <button v-if="!matching" type="button" class="cta" @click="startMatch">
                        Find My Best System →
                    </button>
                    <p v-if="!matching" class="fine">Takes under two minutes.</p>

                    <div v-else class="walk">
                        <TreeWalker
                            v-show="!outcome"
                            ref="walker"
                            :tree="tree"
                            why-variant="inline"
                            data-use-note="Your answers stay on this page unless you choose to send them to a pro."
                            @outcome="onOutcome"
                        />

                        <div v-if="outcome" class="verdict">
                            <strong>{{ outcome.prompt }}</strong>
                            <p>{{ outcome.body }}</p>
                            <ol v-if="outcome.steps?.length">
                                <li v-for="s in outcome.steps" :key="s">{{ s }}</li>
                            </ol>
                            <RouterLink class="cta" to="/second-opinion">
                                Get a Free Second Opinion →
                            </RouterLink>
                            <p class="fine">Your answers ride along, so the pro starts informed.</p>
                            <button type="button" class="restart" @click="restartMatch">
                                Start over
                            </button>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    </section>

    <section class="table-band">
        <div class="table-inner">
            <div>
                <h2>System feature comparison</h2>
                <div class="table-scroll" tabindex="0" role="region" aria-label="System feature comparison table">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col">Feature</th>
                                <th scope="col">Single-Stage<small>Good</small></th>
                                <th scope="col">Variable-Speed<small>Better</small></th>
                                <th scope="col">Communicating<small>Best</small></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in FEATURES" :key="row.feature">
                                <th scope="row">
                                    {{ row.feature }}
                                    <small v-if="row.note">{{ row.note }}</small>
                                </th>
                                <td v-for="(score, i) in row.scores" :key="i">
                                    <span class="stars" :aria-label="`${score} out of 5`">
                                        <i v-for="n in 5" :key="n" :class="{ on: n <= score }">★</i>
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Upfront investment</th>
                                <td v-for="t in TIERS" :key="t.id" class="cost">{{ t.cost }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p class="scroll-hint">Swipe the table sideways to see all three tiers →</p>
                <p class="footnote">
                    Ratings compare the three tiers with each other and nothing else. They are not
                    measurements, and a good installation on cheap equipment beats a poor
                    installation on expensive equipment every time.
                </p>
            </div>

            <aside class="florida">
                <h2>Why it matters in Florida</h2>
                <div v-for="f in FLORIDA" :key="f.title" class="fl">
                    <span class="chip">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="f.icon"></svg>
                    </span>
                    <div>
                        <strong>{{ f.title }}</strong>
                        <p>{{ f.body }}</p>
                    </div>
                </div>

                <div class="help">
                    <h3>Need help choosing?</h3>
                    <p>A local expert can size the house properly and quote more than one tier.</p>
                    <RouterLink class="cta" to="/second-opinion">
                        Connect With an HVAC Professional →
                    </RouterLink>
                </div>
            </aside>
        </div>
    </section>

    <section class="benefits">
        <div class="benefits-inner">
            <h2>What upgrading actually buys you</h2>
            <div class="benefit-grid">
                <div v-for="b in BENEFITS" :key="b.title" class="benefit">
                    <span class="chip">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="b.icon"></svg>
                    </span>
                    <strong>{{ b.title }}</strong>
                    <p>{{ b.body }}</p>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    position: relative;
    background-size: cover;
    background-position: center;
    color: #fff;
}

.wash {
    position: absolute;
    inset: 0;
    background: var(--site-gradient-hero-overlay);
}

.hero-inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20rem;
    gap: var(--site-space-xl);
    align-items: center;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.8rem, 3.6vw, 2.7rem);
    font-weight: 800;
    line-height: 1.14;
    margin: 0 0 var(--site-space-md);
    text-shadow: 0 2px 18px rgba(3, 15, 40, 0.45);
}

.hl {
    color: var(--site-accent);
}

.lede {
    max-width: 36rem;
    margin: 0 0 var(--site-space-lg);
    color: rgba(255, 255, 255, 0.88);
    line-height: 1.6;
}

.badges {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    font-size: 0.8rem;
    font-weight: 600;

    li::before {
        content: '✓';
        margin-right: 0.4rem;
        color: #4ade80;
    }
}

.insist {
    background: rgba(7, 28, 69, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    h2 {
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        margin: 0 0 var(--site-space-sm);
        color: #fff;
    }

    ul {
        list-style: none;
        margin: 0 0 var(--site-space-sm);
        padding: 0;
        display: grid;
        gap: 0.45rem;
        font-size: 0.8rem;
        line-height: 1.4;
    }

    li::before {
        content: '✓';
        margin-right: 0.45rem;
        color: #4ade80;
        font-weight: 700;
    }

    .note {
        margin: 0;
        font-size: 0.72rem;
        color: rgba(255, 255, 255, 0.66);
    }
}

/* ---- tiers ---- */
h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 var(--site-space-lg);
}

.centered {
    text-align: center;
}

.compare-inner,
.benefits-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

.tiers {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) 18rem;
    gap: var(--site-space-md);
    // Cards stretch so "Best for" and the cost row line up across the three
    // tiers; the matcher is a sidebar, not a fourth tier, so it opts out.
    align-items: stretch;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
}

.tier {
    position: relative;
    padding: var(--site-space-lg) var(--site-space-md) var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    display: flex;
    flex-direction: column;

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        color: var(--site-navy);
        margin: 0.4rem 0 0.2rem;
        line-height: 1.3;
    }

    .tagline {
        margin: 0 0 var(--site-space-md);
        font-size: 0.8rem;
        color: var(--site-text-muted);
    }

    ul {
        list-style: none;
        margin: 0 0 var(--site-space-md);
        padding: 0;
        display: grid;
        gap: 0.4rem;
        font-size: 0.84rem;
    }

    li::before {
        content: '✓';
        margin-right: 0.45rem;
        font-weight: 700;
    }

    &[data-tier='single'] li::before {
        color: var(--site-green);
    }

    &[data-tier='variable'] li::before {
        color: var(--site-blue);
    }

    &[data-tier='communicating'] li::before {
        color: var(--site-accent);
    }
}

.ribbon {
    position: absolute;
    top: -0.7rem;
    left: var(--site-space-md);
    padding: 0.15rem 0.7rem;
    border-radius: var(--site-radius-pill);
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    color: #fff;

    [data-tier='single'] & {
        background: var(--site-green);
    }

    [data-tier='variable'] & {
        background: var(--site-blue);
    }

    [data-tier='communicating'] & {
        background: var(--site-accent);
    }
}

.best {
    margin-top: auto;
    padding: 0.6rem 0.7rem;
    border-radius: var(--site-radius-sm);
    background: var(--site-bg-alt);
    font-size: 0.8rem;
    line-height: 1.45;

    strong {
        display: block;
        color: var(--site-navy);
    }
}

.price {
    margin-top: var(--site-space-md);
    padding-top: var(--site-space-sm);
    border-top: 1px dashed var(--site-border);

    .label {
        display: block;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--site-text-muted);
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 1.15rem;
        color: var(--site-navy);
    }

    .tierprice {
        color: var(--site-green);
        letter-spacing: 0.05em;
    }

    small {
        display: block;
        margin-top: 0.2rem;
        font-size: 0.7rem;
        color: var(--site-text-muted);
        line-height: 1.4;
    }
}

/* ---- matcher ---- */
.matcher {
    align-self: start;
    padding: var(--site-space-lg) var(--site-space-md);
    border-radius: var(--site-radius-md);
    background: var(--site-blue-soft);
    border: 1px solid var(--site-border);

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        color: var(--site-navy);
        margin: 0 0 0.3rem;
    }

    > p {
        margin: 0 0 var(--site-space-md);
        font-size: 0.84rem;
        color: var(--site-text-muted);
        line-height: 1.5;
    }

    @media (max-width: 1100px) {
        grid-column: 1 / -1;
    }
}

.criteria {
    list-style: none;
    margin: 0 0 var(--site-space-md);
    padding: 0;
    display: grid;
    gap: 0.4rem;
    font-size: 0.84rem;

    li {
        padding: 0.5rem 0.7rem;
        background: var(--site-surface);
        border-radius: var(--site-radius-sm);
        color: var(--site-navy);
    }
}

.cta {
    display: block;
    padding: 0.7rem 1rem;
    text-align: center;
    border: none;
    width: 100%;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-family: var(--site-font-body);
    font-weight: 700;
    font-size: 0.9rem;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        background: var(--site-accent-strong);
    }

    @media (max-width: 860px) {
        min-height: 48px;
    }
}

.fine {
    margin: 0.45rem 0 0;
    text-align: center;
    font-size: 0.72rem;
    color: var(--site-text-muted);
}

.walk {
    background: var(--site-surface);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);
}

.verdict {
    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        color: var(--site-navy);
        margin-bottom: 0.4rem;
    }

    p {
        margin: 0 0 var(--site-space-sm);
        font-size: 0.85rem;
        line-height: 1.55;
    }

    ol {
        margin: 0 0 var(--site-space-md);
        padding-left: 1.1rem;
        display: grid;
        gap: 0.35rem;
        font-size: 0.82rem;
    }
}

.restart {
    display: block;
    margin: 0.6rem auto 0;
    background: none;
    border: none;
    font-family: var(--site-font-body);
    font-size: 0.78rem;
    color: var(--site-text-muted);
    cursor: pointer;

    &:hover {
        color: var(--site-blue);
    }
}

/* ---- table ---- */
.table-band {
    background: var(--site-bg-alt);
}

.table-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 21rem;
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        /* minmax(0, 1fr), NOT 1fr. A bare `1fr` track carries an implicit
           min-width:auto, so it refuses to shrink below the table's
           min-content width — which dragged the whole page out to 455px
           against a 375px phone viewport and gave every page on this route
           a horizontal scrollbar. The table has its own scroll container;
           this is what lets it do its job. */
        grid-template-columns: minmax(0, 1fr);
    }

    /* Same reason, one level down: the grid item wrapping the table. */
    > div {
        min-width: 0;
    }
}

.table-scroll {
    min-width: 0;
    overflow-x: auto;
    /* Momentum scrolling on iOS, and a contained overscroll so swiping the
       table sideways doesn't start dragging the page. */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
}

/* Below the comfortable width the table scrolls sideways instead of
   crushing four columns into 90px each. The hint appears only when that's
   actually happening. */
.scroll-hint {
    display: none;
    margin: 0.5rem 0 0;
    font-size: 0.78rem;
    color: var(--site-text-muted);

    @media (max-width: 700px) {
        display: block;
    }
}

table {
    width: 100%;
    border-collapse: collapse;
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    overflow: hidden;
    font-size: 0.84rem;
}

thead th {
    padding: 0.7rem 0.8rem;
    background: var(--site-navy);
    color: #fff;
    font-family: var(--site-font-heading);
    font-size: 0.8rem;
    text-align: center;

    &:first-child {
        text-align: left;
    }

    small {
        display: block;
        font-family: var(--site-font-body);
        font-size: 0.68rem;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.7);
    }
}

tbody th {
    padding: 0.6rem 0.8rem;
    text-align: left;
    font-weight: 600;
    color: var(--site-navy);
    border-top: 1px solid var(--site-border);
    max-width: 16rem;

    small {
        display: block;
        font-weight: 400;
        font-size: 0.72rem;
        color: var(--site-text-muted);
        line-height: 1.4;
        margin-top: 0.15rem;
    }
}

tbody td {
    padding: 0.6rem 0.8rem;
    text-align: center;
    border-top: 1px solid var(--site-border);
}

.stars i {
    font-style: normal;
    color: var(--site-border);

    &.on {
        color: var(--site-accent);
    }
}

.cost {
    font-family: var(--site-font-heading);
    font-weight: 700;
    color: var(--site-green);
}

.footnote {
    margin: var(--site-space-md) 0 0;
    font-size: 0.76rem;
    color: var(--site-text-muted);
    line-height: 1.55;
}

/* ---- florida ---- */
.florida {
    h2 {
        font-size: 1.15rem;
        margin-bottom: var(--site-space-md);
    }
}

.fl {
    display: flex;
    gap: 0.7rem;
    margin-bottom: var(--site-space-md);

    .chip {
        display: grid;
        place-items: center;
        width: 2.2rem;
        height: 2.2rem;
        flex-shrink: 0;
        border-radius: var(--site-radius-sm);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        color: var(--site-blue);

        svg {
            width: 1.15rem;
            height: 1.15rem;
        }
    }

    strong {
        display: block;
        font-size: 0.88rem;
        color: var(--site-navy);
    }

    p {
        margin: 0.15rem 0 0;
        font-size: 0.8rem;
        color: var(--site-text-muted);
        line-height: 1.5;
    }
}

.help {
    margin-top: var(--site-space-lg);
    padding: var(--site-space-md);
    border-radius: var(--site-radius-md);
    background: var(--site-gradient-navy);
    color: #fff;

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        margin: 0 0 0.3rem;
    }

    p {
        margin: 0 0 var(--site-space-md);
        font-size: 0.82rem;
        color: rgba(255, 255, 255, 0.82);
        line-height: 1.5;
    }
}

/* ---- benefits ---- */
.benefit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--site-space-lg);
}

.benefit {
    .chip {
        display: grid;
        place-items: center;
        width: 2.8rem;
        height: 2.8rem;
        margin-bottom: 0.6rem;
        border-radius: var(--site-radius-md);
        background: var(--site-blue-soft);
        color: var(--site-blue);

        svg {
            width: 1.4rem;
            height: 1.4rem;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.94rem;
        color: var(--site-navy);
        margin-bottom: 0.25rem;
    }

    p {
        margin: 0;
        font-size: 0.82rem;
        color: var(--site-text-muted);
        line-height: 1.55;
    }
}
</style>
